import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, Pause } from 'lucide-react';
import { getAudioFile, type AudioFile } from '../services/api';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import MiniWaveform from './MiniWaveform';

const formatTime = (s: number): string => {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

interface HeroPlayerDemoProps {
  fileId: string;
  /** Show the "Preview · This is how your shared track will look" caption.
   *  Off for real-track contexts (e.g. the public profile page). */
  showCaption?: boolean;
}

const HeroPlayerDemo: React.FC<HeroPlayerDemoProps> = ({ fileId, showCaption = true }) => {
  const [meta, setMeta] = useState<AudioFile | null>(null);
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    loadTrack,
    togglePlayPause,
    seek,
  } = useAudioPlayer();

  const isCurrent = currentTrack?.id === fileId;
  const isPlayingThis = isCurrent && isPlaying;

  useEffect(() => {
    let cancelled = false;
    getAudioFile(fileId)
      .then((m) => {
        if (!cancelled) setMeta(m);
      })
      .catch(() => {
        // Card still renders with the fileId fallback.
      });
    return () => {
      cancelled = true;
    };
  }, [fileId]);

  const handlePlayClick = () => {
    if (isCurrent) {
      togglePlayPause();
    } else {
      // Force-reload the global player onto the demo track. This pauses + replaces
      // whatever the FloatingPlayer was playing, so there is only ever one stream.
      void loadTrack(fileId, true, true);
    }
  };

  const totalDuration = isCurrent && duration > 0 ? duration : (meta?.durationSeconds || 0);
  const displayCurrentTime = isCurrent ? currentTime : 0;
  const progress = totalDuration > 0 ? displayCurrentTime / totalDuration : 0;

  // Pending seek — set when the user scrubs before the track is the
  // current global player track. Once loadTrack() promotes this file to
  // current AND duration lands, the effect below fires the queued seek
  // once and clears the ref. Without this, a fresh visitor's scrub does
  // nothing (the old guard `if (!isCurrent) return` swallowed it) and
  // the waveform read as broken.
  const pendingSeekRef = useRef<number | null>(null);

  useEffect(() => {
    if (isCurrent && pendingSeekRef.current !== null && totalDuration > 0) {
      seek(pendingSeekRef.current * totalDuration);
      pendingSeekRef.current = null;
    }
  }, [isCurrent, totalDuration, seek]);

  const handleSeek = (next: number) => {
    if (isCurrent && totalDuration > 0) {
      seek(next * totalDuration);
      return;
    }
    // Not the current track yet — load it (autoplay + force) and defer
    // the seek until the effect above sees isCurrent flip true with a
    // real duration in hand.
    pendingSeekRef.current = next;
    void loadTrack(fileId, true, true);
  };

  const artworkUrl = meta?.artworkUrl;
  const title = meta?.title || meta?.filename?.replace(/\.[^.]+$/, '') || 'Sample track';

  return (
    <motion.div
      className="w-full"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      {showCaption && (
        <p
          className="text-[10px] uppercase tracking-[0.2em] text-center mb-2"
          style={{ color: 'var(--text-tertiary)' }}
        >
          Preview · This is how your shared track will look
        </p>
      )}

      <div
        className="rounded-2xl border backdrop-blur-md p-3 md:p-5 flex items-center gap-3 md:gap-5 shadow-lg"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.35)',
          borderColor: 'rgba(255, 255, 255, 0.10)',
        }}
      >
        <button
          type="button"
          onClick={handlePlayClick}
          aria-label={isPlayingThis ? 'Pause preview' : 'Play preview'}
          className="relative flex-shrink-0 w-20 h-20 md:w-28 md:h-28 rounded-xl overflow-hidden border border-white/10 group/btn focus:outline-none focus-visible:ring-2 focus-visible:ring-soundwave-500"
          style={{
            background: artworkUrl
              ? `url(${artworkUrl}) center/cover, #111`
              : 'linear-gradient(135deg, #ff4800 0%, #06b6d4 100%)',
          }}
        >
          <span className="absolute inset-0 bg-black/40 group-hover/btn:bg-black/55 transition-colors flex items-center justify-center">
            {isPlayingThis ? (
              <Pause className="w-7 h-7 md:w-9 md:h-9 text-white" fill="white" strokeWidth={0} />
            ) : (
              <Play className="w-7 h-7 md:w-9 md:h-9 text-white translate-x-0.5" fill="white" strokeWidth={0} />
            )}
          </span>
        </button>

        <div className="flex-1 min-w-0">
          <Link
            to={`/player/${fileId}`}
            className="block text-sm md:text-base font-semibold truncate hover:underline"
            style={{ color: 'var(--text-primary)' }}
            title={title}
          >
            {title}
          </Link>
          <div className="mt-1.5">
            <MiniWaveform
              fileId={fileId}
              progress={progress}
              isActive={isPlayingThis}
              height={40}
              onSeek={handleSeek}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[11px] tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
              {formatTime(displayCurrentTime)} / {formatTime(totalDuration)}
            </span>
            <Link
              to={`/player/${fileId}`}
              className="text-[11px] hover:underline"
              style={{ color: 'var(--text-tertiary)' }}
            >
              Open in player →
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default HeroPlayerDemo;
